import { CARD, HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  type CopyStrings,
  WordRows,
} from "@/components/tools/vocabulary/word-parts";
import { t } from "@/lib/i18n/interpolate";
import {
  definitionLine,
  type HomophoneEntry,
  type HomophoneGroup,
} from "@/lib/tool-kit/word-knowledge/homophones";

export interface HomophoneGroupStrings extends CopyStrings {
  groupHeading: string;
  groupHeadingPlain: string;
  arpabetNote: string;
  uncommonSummary: string;
  posNames: Readonly<Record<string, string>>;
}

function rowsOf(
  entries: readonly HomophoneEntry[],
  posNames: Readonly<Record<string, string>>
) {
  return entries.map((entry) => ({
    word: entry.word,
    detail: definitionLine(entry, posNames) || undefined,
  }));
}

/** One pronunciation of the searched word and the words that share it. */
export function HomophoneGroupCard({
  group,
  strings,
  onSelect,
}: {
  group: HomophoneGroup;
  strings: HomophoneGroupStrings;
  onSelect: (word: string) => void;
}) {
  const heading = group.ipa
    ? t(strings.groupHeading, { ipa: group.ipa })
    : strings.groupHeadingPlain;
  return (
    <section className={`${CARD} space-y-3`}>
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>{heading}</h3>
        <p className={HINT}>
          {t(strings.arpabetNote, { arpabet: group.pron })}
        </p>
      </div>
      {group.common.length > 0 ? (
        <WordRows
          copyStrings={strings}
          onSelect={onSelect}
          rows={rowsOf(group.common, strings.posNames)}
        />
      ) : null}
      {group.uncommon.length > 0 ? (
        <details className="space-y-3">
          <summary className="cursor-pointer text-muted-foreground text-sm">
            {t(strings.uncommonSummary, {
              count: String(group.uncommon.length),
            })}
          </summary>
          <WordRows
            copyStrings={strings}
            onSelect={onSelect}
            rows={rowsOf(group.uncommon, strings.posNames)}
          />
        </details>
      ) : null}
    </section>
  );
}

/** A set of commonly confused homophones, each word a button, with its tip. */
export function ConfusedSetList({
  sets,
  onSelect,
}: {
  sets: readonly { words: readonly string[]; tip: string }[];
  onSelect: (word: string) => void;
}) {
  return (
    <ul className="grid gap-2 sm:grid-cols-2">
      {sets.map((set) => (
        <li
          className="rounded-lg border border-border bg-card px-3 py-2"
          key={set.words.join("|")}
        >
          <p className="flex flex-wrap gap-x-1 font-medium text-sm">
            {set.words.map((word, position) => (
              <span key={word}>
                {position > 0 ? (
                  <span
                    aria-hidden="true"
                    className="me-1 text-muted-foreground"
                  >
                    /
                  </span>
                ) : null}
                <button
                  className="text-primary underline-offset-4 hover:underline"
                  onClick={() => onSelect(word)}
                  type="button"
                >
                  {word}
                </button>
              </span>
            ))}
          </p>
          <p className="mt-0.5 text-muted-foreground text-xs leading-relaxed">
            {set.tip}
          </p>
        </li>
      ))}
    </ul>
  );
}
